import React from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Image as ImageIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowBack as ArrowLeftIcon,
  ArrowForward as ArrowRightIcon,
  Save as SaveIcon,
  DragIndicator as DragIndicatorIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import {
  HoneyContainer,
  HoneyGalleryHeader,
  HoneyGalleryTitle,
  HoneyGallerySubtitle,
  ImageCount,
  HoneyButton,
  HoneyDropZone,
  HoneyGallery,
  HoneyImageCard,
  HoneyImageContent,
  HoneyImageContainer,
  HoneyPositionControls,
  HoneyPositionDisplay,
  HoneyPositionButton,
  HoneyPositionNumber,
  HoneyActionButtons,
  HoneyActionButton,
  HoneyEmptyState,
  HoneyPreviewOverlay,
  HoneyDescription,
  HoneySubtitle,
  HoneyImageText,
  honeyColors
} from './styled';

const GalleryView = ({
  orderedImages,
  orderedNewImages,
  isLoadingState,
  previewImage,
  error,
  snackbarState,
  disableSaveButton,
  getImageIdentifier,
  getImageUrl,
  handleImageUpload,
  handlePreviewImage,
  handleClosePreview,
  handleMoveLeft,
  handleMoveRight,
  handleImageDelete,
  handleSaveGallery,
  handleCloseSnackbar
}) => {
  return (
    <HoneyContainer sx={{ pt: 3 }}>
      <HoneyGalleryHeader>
        <HoneyGalleryTitle>
          <ImageIcon />
          Gallery
        </HoneyGalleryTitle>
        <HoneyGallerySubtitle>
          Visual showcase of this coworking space
        </HoneyGallerySubtitle>
      </HoneyGalleryHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}      
      
      {/* Drop zone for adding images */}
      <HoneyDropZone component="label">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Box sx={{ fontSize: '1.5rem' }}>📁</Box>
          <Typography sx={{ fontWeight: 600, color: honeyColors.honeyBrown }}>
            Drop images here or click to browse
          </Typography>
        </Box>
        <input
          type="file"
          hidden
          accept="image/*"
          multiple
          onChange={handleImageUpload}
        />
      </HoneyDropZone>

      {(orderedImages.length > 0 || orderedNewImages.length > 0) && (
        <Typography sx={{ 
          textAlign: 'center', 
          color: honeyColors.lightBrown || '#8B7355',
          fontWeight: 500,
          mb: 2
        }}>
          {orderedImages.length + orderedNewImages.length} images in gallery
        </Typography>
      )}
      
      {/* Image Gallery */}
      {(orderedImages.length > 0 || orderedNewImages.length > 0) ? (
        <HoneyGallery>
          {/* Display existing images */}
          {orderedImages.map((image, index) => (
            <HoneyImageCard key={getImageIdentifier(image)} isNew={false}>
              <HoneyImageContainer onClick={() => handlePreviewImage(image)}>
                <img
                  src={getImageUrl(image)}
                  alt={image.name || `Image ${index + 1}`}
                />
              </HoneyImageContainer>
              
              <HoneyImageContent>
                <HoneyImageText>
                  {image.name || `Image ${index + 1}`}
                </HoneyImageText>
                
                <HoneyPositionControls>
                  <HoneySubtitle>Position</HoneySubtitle>
                  <HoneyPositionDisplay>
                    <HoneyPositionButton
                      size="small"
                      onClick={() => handleMoveLeft(index)}
                      disabled={index === 0 || isLoadingState}
                      title="Move left"
                    >
                      <ArrowLeftIcon fontSize="small" />
                    </HoneyPositionButton>
                    <HoneyPositionNumber>{index + 1}</HoneyPositionNumber>
                    <HoneyPositionButton
                      size="small"
                      onClick={() => handleMoveRight(index)}
                      disabled={index === orderedImages.length - 1 || isLoadingState}
                      title="Move right"
                    >
                      <ArrowRightIcon fontSize="small" />
                    </HoneyPositionButton>
                  </HoneyPositionDisplay>
                </HoneyPositionControls>
              </HoneyImageContent>
              
              <HoneyActionButtons>
                <Tooltip title="Zoom image">
                  <HoneyActionButton
                    size="small"
                    onClick={() => handlePreviewImage(image)}
                  >
                    <ZoomInIcon fontSize="small" />
                  </HoneyActionButton>
                </Tooltip>
                <Tooltip title="Delete image">
                  <HoneyActionButton
                    variant="delete"
                    size="small"
                    onClick={() => handleImageDelete(index)}
                    disabled={isLoadingState}
                  >
                    <DeleteIcon fontSize="small" />
                  </HoneyActionButton>
                </Tooltip>
              </HoneyActionButtons>
            </HoneyImageCard>
          ))}
          
          {/* Display new images */}
          {orderedNewImages.map((image, index) => (
            <HoneyImageCard key={getImageIdentifier(image)} isNew={true}>
              <HoneyImageContainer onClick={() => handlePreviewImage(image)}>
                <img
                  src={image.url}
                  alt={image.name}
                />
              </HoneyImageContainer>
              
              <HoneyImageContent>
                <HoneyImageText>
                  {image.name}
                </HoneyImageText>
                
                <HoneyPositionControls>
                  <HoneySubtitle>Position</HoneySubtitle>
                  <HoneyPositionDisplay>
                    <HoneyPositionButton
                      size="small"
                      onClick={() => handleMoveLeft(index, true)}
                      disabled={index === 0 || isLoadingState}
                      title="Move left"
                    >
                      <ArrowLeftIcon fontSize="small" />
                    </HoneyPositionButton>
                    <HoneyPositionNumber>{orderedImages.length + index + 1}</HoneyPositionNumber>
                    <HoneyPositionButton
                      size="small"
                      onClick={() => handleMoveRight(index, true)}
                      disabled={index === orderedNewImages.length - 1 || isLoadingState}
                      title="Move right"
                    >
                      <ArrowRightIcon fontSize="small" />
                    </HoneyPositionButton>
                  </HoneyPositionDisplay>
                </HoneyPositionControls>
                
                {image.isTemp && (
                  <Typography variant="caption" sx={{ color: honeyColors.goldenHoney, fontWeight: 600 }}>
                    New image (not uploaded yet)
                  </Typography>
                )}
              </HoneyImageContent>
              
              <HoneyActionButtons>
                <Tooltip title="Zoom image">
                  <HoneyActionButton
                    size="small"
                    onClick={() => handlePreviewImage(image)}
                  >
                    <ZoomInIcon fontSize="small" />
                  </HoneyActionButton>
                </Tooltip>
                <Tooltip title="Delete image">
                  <HoneyActionButton
                    variant="delete"
                    size="small"
                    onClick={() => handleImageDelete(index, true)}
                    disabled={isLoadingState}
                  >
                    <DeleteIcon fontSize="small" />
                  </HoneyActionButton>
                </Tooltip>
              </HoneyActionButtons>
            </HoneyImageCard>
          ))}
        </HoneyGallery>
      ) : (
        <HoneyEmptyState>
          <PhotoCameraIcon sx={{ fontSize: 48, color: honeyColors.honeycomb, mb: 2 }} />
          <Typography variant="body1" sx={{ color: honeyColors.honeyBrown }}>
            No images in this gallery yet.
          </Typography>
          <Typography variant="body2" sx={{ color: honeyColors.lightBrown || '#8B7355', mt: 1 }}>
            Upload your first image above
          </Typography>
        </HoneyEmptyState>
      )}

      {/* Save Button */}
      {!disableSaveButton && orderedNewImages.filter(img => img.isTemp).length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <HoneyButton
            className="primary"
            onClick={handleSaveGallery}
            disabled={isLoadingState}
            startIcon={isLoadingState ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              background: honeyColors.honeyGradient,
              color: honeyColors.honeyBrown,
              borderRadius: '50px',
              padding: '12px 32px',
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: `0 8px 32px ${honeyColors.goldenHoney}50`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: honeyColors.honeycombGradient,
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${honeyColors.goldenHoney}70`,
              },
              '&:disabled': {
                opacity: 0.6,
                transform: 'none',
              },
            }}
          >
            {isLoadingState ? 'Uploading...' : '💾 Save Changes'}
          </HoneyButton>
        </Box>
      )}
      
      {/* Image Preview Dialog */}
      {previewImage && (
        <HoneyPreviewOverlay onClick={handleClosePreview}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              color: 'white',
              backgroundColor: honeyColors.honeyBrown,
              '&:hover': {
                backgroundColor: honeyColors.darkHoney,
              },
            }}
            onClick={handleClosePreview}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={getImageUrl(previewImage)}
            alt={previewImage.name || 'Image preview'}
            style={{
              maxWidth: '90%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: '12px',
              boxShadow: `0 20px 60px ${honeyColors.honeyBrown}60`,
            }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'white', 
              mt: 2, 
              textShadow: `2px 2px 4px ${honeyColors.honeyBrown}`,
              fontWeight: 600
            }}
          >
            {previewImage.name || 'Image preview'}
          </Typography>
        </HoneyPreviewOverlay>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarState.severity}
          variant="filled"
          sx={{
            backgroundColor: snackbarState.severity === 'success' 
              ? honeyColors.goldenHoney 
              : undefined,
            color: snackbarState.severity === 'success' 
              ? honeyColors.honeyBrown 
              : undefined,
          }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </HoneyContainer>
  );
};

export default GalleryView;